import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../userContext';
import { Menu, Transition } from '@headlessui/react';
import { UserGroupIcon, ChevronDownIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClient } from '../clientContext';
import performanceMonitor from '../utils/performanceMonitor';

export default function ClientSelector({ sidebarExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessProfile, switchActiveClient, agencyId, userProfile } = useUser();
  const { clients, refreshClients } = useClient();
  const [originalCompany, setOriginalCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (userProfile?.companyId) {
      setOriginalCompany(userProfile.companyId);
      setIsLoading(false);
    }
  }, [userProfile]);

  const isImpersonating = !isLoading && businessProfile?.id !== originalCompany;

  const getDropdownPosition = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`
    };
  };

  // Handle client switch with proper navigation and performance tracking
  const handleClientSwitch = async (client) => {
    // Track client switching performance
    performanceMonitor.startNavigation(`client_switch_${client.name}`);
    
    try {
      await switchActiveClient(client);
      
      // Always redirect to dashboard when switching clients
      navigate('/studio/dashboard');
      
      // Track successful switch
      performanceMonitor.trackCacheHit('client_switch', true);
      
    } catch (error) {
      console.error('Error switching client:', error);
      performanceMonitor.trackCacheHit('client_switch', false);
    } finally {
      performanceMonitor.endNavigation(`client_switch_${client.name}`);
    }
  };

  // Handle exit client view with performance tracking
  const handleExitClientView = async () => {
    performanceMonitor.startNavigation('exit_client_view');
    
    try {
      await switchActiveClient(null);
      performanceMonitor.trackCacheHit('client_exit', true);
    } catch (error) {
      console.error('Error exiting client view:', error);
      performanceMonitor.trackCacheHit('client_exit', false);
    } finally {
      performanceMonitor.endNavigation('exit_client_view');
    }
  };

  return (
    <div className="relative" style={{ isolation: 'isolate' }}>
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button 
              ref={buttonRef}
              className={`
                w-full inline-flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-lg transition-all duration-200
                ${isLoading 
                  ? 'text-gray-300 bg-gray-800 dark:text-gray-200 dark:bg-gray-800'
                  : isImpersonating 
                    ? 'text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg'
                    : 'text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-600'
                } focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-gray-800
              `}
              style={{
                fontFamily: '"Share Tech Mono", monospace',
                textShadow: isImpersonating ? '1px 1px 0px rgba(0,0,0,0.3)' : 'none'
              }}
            >
              {!sidebarExpanded ? (
                <UserGroupIcon className={`h-5 w-5 mx-auto ${isImpersonating ? 'text-white' : 'text-gray-400'}`} />
              ) : (
                <>
                  <span className="flex items-center gap-2 truncate flex-1 text-left">
                    {isImpersonating ? (
                      <>
                        {businessProfile?.headlinePhoto ? (
                          <img 
                            src={businessProfile.headlinePhoto} 
                            alt={`${businessProfile.name} logo`}
                            className="h-5 w-5 flex-shrink-0 rounded-sm object-contain"
                          />
                        ) : (
                          <UserCircleIcon className="h-5 w-5 text-white" />
                        )}
                        <span className="truncate text-xs uppercase tracking-wider">{businessProfile?.name}</span>
                      </>
                    ) : (
                      <>
                        <UserGroupIcon className="h-5 w-5 text-gray-400" />
                        <span className="truncate text-xs uppercase tracking-wider">SWITCH CLIENT</span>
                      </>
                    )}
                  </span>
                  <ChevronDownIcon 
                    className={`h-4 w-4 flex-shrink-0 ${
                      isImpersonating ? 'text-white' : 'text-gray-400'
                    }`} 
                    aria-hidden="true" 
                  />
                </>
              )}
            </Menu.Button>

            {open && createPortal(
              <Transition
                show={open}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  className={`
                    fixed z-[2147483647] rounded-xl shadow-2xl ring-1 ring-black ring-opacity-20 focus:outline-none
                    ${sidebarExpanded ? 'w-64' : 'w-52'}
                  `}
                  style={{
                    ...getDropdownPosition(),
                    background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
                    border: '2px solid rgba(241, 196, 15, 0.3)',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                >
                  <div className="py-2">
                    {isImpersonating && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleExitClientView}
                            className={`
                              flex items-center gap-3 w-full px-4 py-3 text-sm font-bold transition-all duration-200
                              ${active 
                                ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-md' 
                                : 'text-violet-400 hover:text-violet-300'
                              }
                              border-b border-gray-600 mb-2
                            `}
                            style={{
                              textShadow: active ? '1px 1px 0px rgba(0,0,0,0.3)' : 'none'
                            }}
                          >
                            <ArrowLeftIcon className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">← EXIT CLIENT VIEW</span>
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    
                    {clients.length > 0 ? (
                      <div className="space-y-1 px-2">
                        {clients.map((client) => (
                          <Menu.Item key={client.id}>
                            {({ active }) => (
                              <button
                                onClick={() => handleClientSwitch(client)}
                                className={`
                                  flex items-center w-full px-4 py-3 text-sm font-bold transition-all duration-200 rounded-lg
                                  ${businessProfile?.id === client.id 
                                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md' 
                                    : active
                                    ? 'bg-gradient-to-r from-gray-700 to-gray-600 text-white'
                                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                  }
                                `}
                                style={{
                                  textShadow: (businessProfile?.id === client.id || active) ? '1px 1px 0px rgba(0,0,0,0.3)' : 'none'
                                }}
                              >
                                {client.headlinePhoto ? (
                                  <img 
                                    src={client.headlinePhoto} 
                                    alt={`${client.name} logo`}
                                    className="h-5 w-5 mr-3 flex-shrink-0 rounded-sm object-contain"
                                  />
                                ) : (
                                  <UserCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                                )}
                                <span className="truncate text-xs uppercase tracking-wider">{client.name}</span>
                                {businessProfile?.id === client.id && (
                                  <span className="ml-auto text-xs opacity-75">✓</span>
                                )}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                        
                        {!isImpersonating && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => navigate('/studio/clients')}
                                className={`
                                  flex items-center w-full px-4 py-3 text-sm font-bold transition-all duration-200 rounded-lg mt-3
                                  border-t border-gray-600 pt-4
                                  ${active 
                                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white shadow-md' 
                                    : 'text-yellow-400 hover:text-yellow-300 hover:bg-gray-700'
                                  }
                                `}
                                style={{
                                  textShadow: active ? '1px 1px 0px rgba(0,0,0,0.3)' : 'none'
                                }}
                              >
                                <PlusIcon className="h-4 w-4 mr-3 flex-shrink-0" />
                                <span className="text-xs uppercase tracking-wider">ADD NEW CLIENT</span>
                              </button>
                            )}
                          </Menu.Item>
                        )}
                      </div>
                    ) : (
                      !isImpersonating && (
                        <div className="px-2">
                          <button
                            onClick={() => navigate('/studio/clients')}
                            className="flex items-center w-full px-4 py-3 text-sm font-bold text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 rounded-lg transition-all duration-200"
                            style={{
                              fontFamily: '"Share Tech Mono", monospace'
                            }}
                          >
                            <PlusIcon className="h-4 w-4 mr-3 flex-shrink-0" />
                            <span className="text-xs uppercase tracking-wider">ADD FIRST CLIENT</span>
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </Menu.Items>
              </Transition>,
              document.body
            )}
          </>
        )}
      </Menu>
    </div>
  );
} 